// The two things a school prints for a paper: the question paper, and the OMR
// sheet a student marks their answers on.
//
// Both exist only on paper (`hidden print:block`); the editor portals one to
// `document.body` for the length of a print. The ids `cwy-print-paper` and
// `cwy-print-omr` are load-bearing: the `@media print` rules in globals.css key
// off them to drop `#root` from the flow (hidden with `visibility` alone, the
// app behind still took its space and printed a two-page paper as ten) and to
// print these two portrait.
//
// The OMR follows the call's spec: the paper code a scanner reads, a roll
// number to bubble, a row of choice bubbles per objective question, a row of
// mark circles per subjective one (0 to its marks, in half steps when the paper
// uses half marks), and "Filled by" / "Checked by" — the second peer's
// signature is what makes a peer-marked sheet trustworthy. No QR: descoped, so
// the code is printed as digits a person and a scanner can both read.
import { paperSeconds, wordLimitFor } from '@cwy/config/contracts';
import type { CustomPaperItem } from '@/types';
import { MathText } from '@/components/exam/MathText';

export interface PrintHeader {
  schoolName: string;
  title: string;
  classroomName: string;
  subjectName: string;
  /** `paperCodeFor()` of the published paper, so a sheet and its report agree. */
  paperCode: string;
}

const SECTION_LABEL: Record<string, string> = {
  MCQ: 'Multiple choice',
  SHORT_DESC: 'Short answer',
  MEDIUM_DESC: 'Medium answer',
  LONG_DESC: 'Long answer',
};

const ink = { color: '#111' };

/**
 * Circles past this stop fitting a row, which is why the call put subjective
 * questions at 3 marks. Longer questions still print, with a note: how they
 * should go on an OMR is an open question with the client.
 */
export const OMR_MAX_CIRCLES = 7;

export function markSteps(marks: number, half: boolean): number[] {
  const step = half ? 0.5 : 1;
  const out: number[] = [];
  for (let m = 0; m <= marks + 1e-9; m += step) out.push(Math.round(m * 2) / 2);
  return out;
}

function CodeBoxes({ code }: { code: string }) {
  return (
    <span className="inline-flex gap-1">
      {code.split('').map((d, i) => (
        <span
          key={i}
          className="flex h-7 w-6 items-center justify-center border text-base font-bold"
          style={{ borderColor: '#111', ...ink }}
        >
          {d}
        </span>
      ))}
    </span>
  );
}

function SheetHeader({ header, what }: { header: PrintHeader; what: string }) {
  return (
    <div
      className="mb-4 flex items-start justify-between gap-4 border-b-2 pb-3"
      style={{ borderColor: '#111' }}
    >
      <div>
        <p className="text-xs font-bold uppercase tracking-wider" style={ink}>
          {header.schoolName}
        </p>
        <p className="text-lg font-bold" style={ink}>
          {header.title}
        </p>
        <p className="text-sm" style={ink}>
          {[header.classroomName, header.subjectName, what].filter(Boolean).join(' · ')}
        </p>
      </div>
      <div className="text-right">
        <p className="mb-1 text-fs-10 font-bold uppercase tracking-wider" style={ink}>
          Paper code
        </p>
        <CodeBoxes code={header.paperCode} />
      </div>
    </div>
  );
}

/** The question paper, numbered as the class report numbers it. */
export function PrintablePaper({
  header,
  items,
}: {
  header: PrintHeader;
  items: CustomPaperItem[];
}) {
  const totalMarks = items.reduce((s, i) => s + (i.marks ?? 0), 0);
  // The same figure as the on-screen paper's clock and the editor's "about N min".
  const minutes = Math.round(paperSeconds(items) / 60);
  // Printed in the teacher's order. Sections appear only when she SET the paper
  // in sections (each type in one run); regrouping a mixed paper would have to
  // renumber it, and a paper that says "5" where the report says "Q4" sends a
  // teacher hunting.
  const runs: { type: string; rows: CustomPaperItem[] }[] = [];
  for (const item of items) {
    const type = item.type ?? 'SHORT_DESC';
    const last = runs[runs.length - 1];
    if (last?.type === type) last.rows.push(item);
    else runs.push({ type, rows: [item] });
  }
  const inSections = new Set(runs.map((r) => r.type)).size === runs.length;
  const sections = inSections ? runs : [{ type: '', rows: items }];

  return (
    <div id="cwy-print-paper" className="hidden bg-white p-2 print:block" style={ink}>
      <SheetHeader header={header} what="Question paper" />
      <div className="mb-4 flex justify-between text-sm font-semibold">
        <span>Time allowed: {minutes} minutes</span>
        <span>Maximum marks: {totalMarks}</span>
      </div>
      <p className="mb-4 text-xs">
        All questions are compulsory. Marks for each question are shown against it. Write your roll
        number on the OMR sheet before you begin.
      </p>
      {sections.map((section, s) => (
        <section key={section.type || 'all'} className="mb-5">
          {section.type && (
            <p className="mb-2 text-sm font-bold">
              Section {String.fromCharCode(65 + s)} — {SECTION_LABEL[section.type]}
            </p>
          )}
          <ol className="flex flex-col gap-3">
            {section.rows.map((item) => {
              const limit = wordLimitFor(item);
              return (
                <li
                  key={item.index}
                  className="flex gap-3 text-sm"
                  style={{ breakInside: 'avoid' }}
                >
                  <span className="w-7 flex-none font-bold">{item.index}.</span>
                  <div className="flex-1">
                    <MathText>{item.question}</MathText>
                    {item.type === 'MCQ' && (
                      <div className="mt-1 grid grid-cols-2 gap-x-6 gap-y-0.5">
                        {(item.options ?? []).map((o, i) => (
                          <span key={i}>
                            ({String.fromCharCode(97 + i)}) <MathText>{o}</MathText>
                          </span>
                        ))}
                      </div>
                    )}
                    {limit !== null && (
                      <p className="mt-0.5 text-xs italic">Answer in about {limit} words.</p>
                    )}
                  </div>
                  <span className="flex-none font-semibold">[{item.marks ?? 0}]</span>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}

function Bubble({ label }: { label: string }) {
  return (
    <span
      className="flex h-6 w-6 flex-none items-center justify-center rounded-full border text-fs-10 font-bold"
      style={{ borderColor: '#111', ...ink }}
    >
      {label}
    </span>
  );
}

/** The OMR sheet: roll number, objective bubbles, subjective mark circles, two signatures. */
export function OmrSheet({ header, items }: { header: PrintHeader; items: CustomPaperItem[] }) {
  const objective = items.filter((i) => i.type === 'MCQ');
  const subjective = items.filter((i) => i.type !== 'MCQ');
  // Half marks anywhere on the paper means half-mark circles on every row, so
  // a checker never has to guess which rows allow them.
  const half = items.some((i) => (i.marks ?? 0) % 1 !== 0);
  const overflow = subjective.filter((i) => markSteps(i.marks ?? 0, half).length > OMR_MAX_CIRCLES);

  return (
    <div id="cwy-print-omr" className="hidden bg-white p-2 print:block" style={ink}>
      <SheetHeader header={header} what="OMR answer sheet" />

      <div className="mb-4 grid grid-cols-2 gap-6">
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-wider">Name</p>
          <div className="h-7 border-b" style={{ borderColor: '#111' }} />
        </div>
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-wider">
            Roll number — fill one circle per column
          </p>
          <div className="flex gap-3">
            {[0, 1].map((col) => (
              <div key={col} className="flex flex-col gap-0.5">
                <span className="h-6 w-6 border" style={{ borderColor: '#111' }} />
                {Array.from({ length: 10 }, (_, d) => (
                  <Bubble key={d} label={String(d)} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {objective.length > 0 && (
        <section className="mb-4">
          <p className="mb-2 text-sm font-bold">Objective — fill the circle of your answer</p>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
            {objective.map((item) => (
              <div key={item.index} className="flex items-center gap-2">
                <span className="w-7 text-sm font-bold">{item.index}</span>
                {(item.options?.length ? item.options : ['', '', '', '']).map((_, i) => (
                  <Bubble key={i} label={String.fromCharCode(65 + i)} />
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      {subjective.length > 0 && (
        <section className="mb-4">
          <p className="mb-2 text-sm font-bold">
            Subjective — the checker fills the marks awarded
            {half ? ', in half marks' : ''}
          </p>
          <div className="flex flex-col gap-1.5">
            {subjective.map((item) => (
              <div key={item.index} className="flex items-center gap-2">
                <span className="w-7 text-sm font-bold">{item.index}</span>
                {markSteps(item.marks ?? 0, half).map((m) => (
                  <Bubble key={m} label={String(m)} />
                ))}
                <span className="ml-2 text-xs">out of {item.marks ?? 0}</span>
              </div>
            ))}
          </div>
          {overflow.length > 0 && (
            <p className="mt-2 text-fs-10 italic">
              {overflow.map((i) => `Q${i.index}`).join(', ')} need more than {OMR_MAX_CIRCLES}{' '}
              circles. How 4–5 mark questions go on an OMR is still an open question.
            </p>
          )}
        </section>
      )}

      <div className="mt-6 grid grid-cols-2 gap-8">
        {['Filled by', 'Checked by'].map((label) => (
          <div key={label}>
            <div className="h-8 border-b" style={{ borderColor: '#111' }} />
            <p className="mt-1 text-xs font-bold uppercase tracking-wider">
              {label} — name and signature
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
